// ======================================================
// FILE: src/App.js
// Merged fix:
// - Keep stable routing/shell from backup App.js
// - Keep MFA enforcement (14-day grace + toast) from newer App.js
// - REMOVE verify-email gate entirely (no /verify-email, no sendEmailVerification)
// - DO NOT TOUCH manager mode logic (manager-select, getManagerOwner, etc.)
// ======================================================
import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Videos from "./components/Videos";
import LegacyMessage from "./components/LegacyMessage";
import FuneralPlanning from "./components/FuneralPlanning";

// Tracking / guards / access
import AnalyticsRouteTracker from "./components/AnalyticsRouteTracker";
import { AccessProvider } from "./access/AccessContext";
import { getManagerOwner } from "./lib/managerMode";
import ManagerPageGate from "./access/ManagerPageGate";
import Register from "./components/Register";
import LeadCapture from "./components/LeadCapture";
import LandingChoice from "./components/LandingChoice";

// Shell UI
import TopBar from "./components/TopBar";
import Sidebar from "./components/Sidebar";
import MobileFooter from "./components/MobileFooter";

// Auth & flows
import Auth from "./components/Auth";
import EmailActionHandler from "./components/auth/EmailActionHandler";
import ForgotPassword from "./components/ForgotPassword";
import AcceptInvitation from "./components/AcceptInvitation";
import ManagerRegister from "./pages/ManagerRegister";
import ManagerLogin from "./pages/ManagerLogin";

// Pages
import Homepage from "./components/Homepage";
import Contacts from "./components/Contacts";
import Belongings from "./components/Belongings";
import OrganDonation from "./components/OrganDonation";
import MemoryLane from "./components/MemoryLane";
import ImportantDocuments from "./components/ImportantDocuments";
import Ewill from "./components/Ewill";
import AdvanceHealthcareDirective from "./components/AdvanceHealthcareDirective";
import PasswordVault from "./components/PasswordVault";
import DigitalPlatforms from "./components/DigitalPlatforms";
import Devices from "./components/Devices";
import PersonalMessages from "./components/PersonalMessages";
import TimeVault from "./components/TimeVault";
import LastGoodbyes from "./components/LastGoodbyes";
import ProofOfLife from "./components/ProofOfLife";
import Pricing from "./components/Pricing";
import PersonalPlans from "./components/PersonalPlans";
import ProfilePage from "./components/ProfilePage";
import PersonalProfilePage from "./components/PersonalProfilePage";
import AccountManagers from "./components/AccountManagers";
import InviteManagers from "./components/InviteManagers";
import ManagedAccounts from "./components/ManagedAccounts";
import InviteLanding from "./pages/InviteLanding";
import ManagerWelcome from "./pages/ManagerWelcome";
import AdminDashboard from "./components/AdminDashboard";
import AdminAnalytics from "./pages/AdminAnalytics";
import AdminMetrics from "./components/AdminMetrics";
import AdminLeads from "./pages/AdminLeads";
import AdminReferrals from "./pages/AdminReferrals";
import MyPreferences from "./pages/MyPreferences";
import AdminNursingHomes from "./pages/AdminNursingHomes";
import TenantDetail from "./pages/TenantDetail";
import TenantMemberManage from "./pages/TenantMemberManage";
import FamilySignupPage from "./pages/FamilySignupPage";
import AcceptTenantInvite from "./pages/AcceptTenantInvite";
import ManagerSelect from "./components/ManagerSelect";
import PostPayment from "./pages/PostPayment";
import PaymentRequired from "./components/PaymentRequired";
import Security from "./pages/Security";
import MfaGate from "./components/security/MfaGate";
import ResetPassword from "./components/ResetPassword";
import Pensions from "./components/Pensions";
import { clearSessionBypass } from "./utils/mfa";

// Support pages (user-facing)
import SupportTickets from "./support/SupportTickets";
import TicketDetail from "./support/TicketDetail";

// ✅ Admin Tickets page
import AdminTickets from "./support/AdminTickets";

// Firebase
import { auth, db } from "./firebase";
import { onAuthStateChanged, getIdTokenResult, signOut } from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot
} from "firebase/firestore";

// Styles
import "./styles/fixes.css";
import "./styles/mobile-footer.css";
import "./App.css";
import SignupPage from "./pages/SignupPage";

// ✅ MFA policy (minimum security for sensitive data)
const MFA_GRACE_DAYS = 14;
const MFA_GRACE_MS = MFA_GRACE_DAYS * 24 * 60 * 60 * 1000;

/* Inline hook: desktop check */
function useIsDesktop(breakpoint = 1024) {
  const [isDesktop, setIsDesktop] = useState(
    () => (typeof window !== "undefined" ? window.innerWidth >= breakpoint : true)
  );
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isDesktop;
}

/* Minimal admin guard that waits for auth hydration */
function RequireAdmin({ user, authReady, children }) {
  if (!authReady) return null;

  if (
    user &&
    (
      user.isAdmin ||
      user.role === "platform_admin"
    )
  ) {
    return children;
  }

  return <Navigate to="/" replace />;
}



/* Public/auth routes shown when NO user is logged in */
function PublicRoutes({ onLogin }) {
  return (
    <>
      <Routes>
        <Route path="/" element={<LandingChoice />} />
        <Route path="/login" element={<Auth onLogin={onLogin} />} />
        <Route path="/forgot" element={<ForgotPassword />} />
        <Route path="/personal/plans" element={<PersonalPlans />} />
        <Route path="/plans" element={<Pricing />} />
        <Route path="/signup" element={<SignupPage />} />
              <Route path="/family/signup" element={<FamilySignupPage />} />
        <Route path="/start" element={<LeadCapture />} />
        <Route path="/register" element={<Register />} />
        <Route path="/post-payment" element={<PostPayment />} />
        <Route path="/auth/action" element={<EmailActionHandler />} />
        <Route path="/invite/:id" element={<InviteLanding />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <MobileFooter />
    </>
  );
}

/* Private shell for logged-in users */
function PrivateShell({
  user,
  handleProfileUpdate,
  handleLogout,
  handlePasswordReset,
  sidebarOpen,
  setSidebarOpen,
  mfaEnabled,
}) {
  const isDesktop = useIsDesktop(1024);
  const location = useLocation();

  const [mfaToastDismissed, setMfaToastDismissed] = useState(() => {
    try {
      return sessionStorage.getItem("mfaNudgeDismissed") === "1";
    } catch {
      return false;
    }
  });

  const dismissMfaToast = () => {
    try {
      sessionStorage.setItem("mfaNudgeDismissed", "1");
    } catch {}
    setMfaToastDismissed(true);
  };

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname, setSidebarOpen]);

  const mm = getManagerOwner();
  const isManagerView = !!mm;
  const viewUser = isManagerView ? { ...user, uid: mm.ownerUid, viewingAsManager: true } : user;

  // ✅ strict condition: manager-only (unpaid) users
  // Nursing home accounts are work-only. Personal sections are blocked outright,
  // not merely hidden from the menu, because a work login is handed on when
  // staff leave and personal end-of-life data must not travel with it.
  const isNursingHomeAccount = viewUser?.accountKind === "nursinghome";

  const nhAllowedPrefixes = [
    "/support",
    "/profile",
    "/security",
    "/settings",
    "/payment-required",
    "/logout",
  ];

  // The tenant dashboard itself is allowed, but not its admin sub-pages
  // (billing, tenant support, settings) which are not for care home staff.
  const nhTenantDashboardPath =
    isNursingHomeAccount && viewUser?.primaryTenantId
      ? `/tenant/${viewUser.primaryTenantId}`
      : null;

  const nhPathAllowed =
    !isNursingHomeAccount ||
    location.pathname === nhTenantDashboardPath ||
    nhAllowedPrefixes.some((p) => location.pathname.startsWith(p));

  // Someone who signed up in their own right has a box to go to, even on the
  // free plan. Without this, a free user who accepts a manager invite is sent
  // back to /manager-select every time they open their own account.
  // Mirrors the same check in src/lib/readOnlyGuard.js - keep the two in step.
  const hasOwnAccount =
    !!viewUser &&
    (viewUser.accountTypes?.personal === true ||
      !!viewUser.personalAccount?.plan ||
      !!viewUser.planKey);

  const isManagerOnlyUnpaid =
    !!viewUser &&
    !viewUser.isAdmin &&
    (viewUser.managerOnly === true || viewUser.role === "account_manager") &&
    viewUser.subscriptionStatus !== "active" &&
    !hasOwnAccount;

  return (
    <div className="sh-wrap">
      <TopBar user={viewUser} onMenuClick={() => setSidebarOpen(true)} />

      <div className="sh-body">
        {isDesktop && !isNursingHomeAccount && <Sidebar mode="permanent" user={viewUser} />}
        <main className="sh-main">
          {/* MFA nudge (dismissible per session; never shown once MFA is enabled) */}
          {!mfaEnabled && location.pathname !== "/security" && !mfaToastDismissed ? (
            <div className="mfa-toast-wrap" role="status" aria-live="polite">
              <div className="mfa-toast-dim" onClick={dismissMfaToast} aria-hidden="true" />
              <div className="mfa-toast">
                <div className="mfa-toast__inner">
                  <div className="mfa-toast__title">Secure your account</div>
                  <div className="mfa-toast__text">
                    For account security, we recommend enabling multi-factor authentication (MFA).
                  </div>
                  <div className="mfa-toast__actions">
                    <a className="mfa-toast__btn" href="/security">Set up MFA</a>
                    <button
                      type="button"
                      className="mfa-toast__close"
                      aria-label="Dismiss"
                      onClick={dismissMfaToast}
                    >
                      ×
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {isNursingHomeAccount && !nhPathAllowed && viewUser?.primaryTenantId && (
            <Navigate to={`/tenant/${viewUser.primaryTenantId}`} replace />
          )}
          <Routes>
            {/* ✅ Gate "/" here */}
            <Route
              path="/"
              element={
                isNursingHomeAccount && viewUser?.primaryTenantId ? (
                  <Navigate to={`/tenant/${viewUser.primaryTenantId}`} replace />
                ) : isManagerOnlyUnpaid ? (
                  <Navigate to="/manager-select" replace />
                ) : (
                  <ManagerPageGate pageKey="home">
                    <Homepage user={viewUser} />
                  </ManagerPageGate>
                )
              }
            />

            {/* Account managers */}
            <Route path="/account-managers" element={isManagerView ? <Navigate to="/manager-select" replace /> : <AccountManagers user={viewUser} />} />
            <Route path="/account-managers/invite" element={isManagerView ? <Navigate to="/manager-select" replace /> : <InviteManagers user={viewUser} />} />
            <Route path="/account-managers/managed" element={isManagerView ? <Navigate to="/manager-select" replace /> : <ManagedAccounts user={viewUser} />} />

            {/* Vaults */}
            <Route path="/vaults/digital-platforms" element={<ManagerPageGate pageKey="digital_platforms"><DigitalPlatforms user={viewUser} /></ManagerPageGate>} />
            <Route path="/vaults/devices" element={<ManagerPageGate pageKey="devices"><Devices user={viewUser} /></ManagerPageGate>} />
            <Route path="/vaults/password-vault" element={<ManagerPageGate pageKey="password_vault"><PasswordVault user={viewUser} /></ManagerPageGate>} />

            {/* Messages */}
            <Route path="/messages/personal-messages" element={<ManagerPageGate pageKey="personal_messages"><PersonalMessages user={viewUser} /></ManagerPageGate>} />
            <Route path="/messages/time-vault" element={<ManagerPageGate pageKey="time_vault"><TimeVault user={viewUser} /></ManagerPageGate>} />
            <Route path="/messages/last-goodbyes" element={<ManagerPageGate pageKey="last_goodbyes"><LastGoodbyes user={viewUser} /></ManagerPageGate>} />
            <Route path="/messages/videos" element={<ManagerPageGate pageKey="videos"><Videos user={viewUser} /></ManagerPageGate>} />
            <Route path="/messages/legacy-message" element={<ManagerPageGate pageKey="legacy_message"><LegacyMessage user={viewUser} /></ManagerPageGate>} />

            {/* Legal / Personal */}
            <Route path="/legal/important-documents" element={<ManagerPageGate pageKey="important_documents"><ImportantDocuments user={viewUser} /></ManagerPageGate>} />
            <Route path="/legal/secure-e-will" element={<ManagerPageGate pageKey="secure_e_will"><Ewill user={viewUser} /></ManagerPageGate>} />
            <Route
              path="/legal/advance-healthcare-directive"
              element={<ManagerPageGate pageKey="advance_healthcare_directive"><AdvanceHealthcareDirective user={viewUser} /></ManagerPageGate>}
            />
            <Route path="/legal/pensions" element={<ManagerPageGate pageKey="pensions"><Pensions user={viewUser} /></ManagerPageGate>} />
            <Route path="/personal/funeral-planning" element={<ManagerPageGate pageKey="funeral_planning"><FuneralPlanning user={viewUser} /></ManagerPageGate>} />
            <Route path="/about-me" element={<MyPreferences />} />
            <Route path="/personal/memory-lane" element={<ManagerPageGate pageKey="memory_lane"><MemoryLane user={viewUser} /></ManagerPageGate>} />
            <Route path="/personal/belongings" element={<ManagerPageGate pageKey="belongings"><Belongings user={viewUser} /></ManagerPageGate>} />
            <Route path="/personal/organ-donation" element={<ManagerPageGate pageKey="organ_donation"><OrganDonation user={viewUser} /></ManagerPageGate>} />

            {/* Other */}
            <Route path="/proof-of-life" element={<ManagerPageGate pageKey="proof_of_life"><ProofOfLife user={viewUser} /></ManagerPageGate>} />
            <Route path="/contacts" element={<ManagerPageGate pageKey="contacts"><Contacts user={viewUser} /></ManagerPageGate>} />

            {/* Support */}
            <Route path="/support" element={<ManagerPageGate pageKey="support_ticket"><SupportTickets user={viewUser} /></ManagerPageGate>} />
            <Route path="/support/:ticketId" element={<ManagerPageGate pageKey="support_ticket"><TicketDetail user={viewUser} /></ManagerPageGate>} />
            <Route path="/support/t/:id" element={<ManagerPageGate pageKey="support_ticket"><TicketDetail user={viewUser} /></ManagerPageGate>} />
            <Route path="/plans" element={<Pricing />} />
          <Route path="/personal/plans" element={<PersonalPlans />} />
        <Route path="/signup" element={<SignupPage />} />
              <Route path="/family/signup" element={<FamilySignupPage />} />

            {/* Profile */}
            <Route
              path="/profile"
              element={
                viewUser?.accountTypes?.personal === true && !viewUser?.businessAccount?.tenantId ? (
                  <PersonalProfilePage
                    user={viewUser}
                    onUpdate={handleProfileUpdate}
                    onLogout={handleLogout}
                  />
                ) : (
                  <ProfilePage
                    user={viewUser}
                    onUpdate={handleProfileUpdate}
                    onLogout={handleLogout}
                    onPasswordReset={handlePasswordReset}
                  />
                )
              }
            />

            {/* ✅ Security / MFA settings */}
            <Route
              path="/security"
              element={
                <ManagerPageGate pageKey="security">
                  <Security mfaRequired={false} mfaDaysLeft={0} mfaEnabled={mfaEnabled} />
                </ManagerPageGate>
              }
            />

            <Route path="/manager-welcome" element={<ManagerWelcome user={viewUser} />} />
            <Route path="/manager" element={isManagerView ? <Navigate to="/contacts" replace /> : <Navigate to="/" replace />} />
            <Route path="/tenant/:tenantId" element={<TenantDetail user={viewUser} />} />
            <Route path="/tenant/:tenantId/member/:memberId" element={<TenantMemberManage />} />
            <Route path="/tenant/:tenantId/support" element={<AdminTickets />} />

            {/* Misc */}
            <Route path="/forgot" element={<ForgotPassword />} />
            <Route path="/invite/:id" element={<InviteLanding />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>

      {!isDesktop && !isNursingHomeAccount && (
        <Sidebar mode="overlay" open={sidebarOpen} onClose={() => setSidebarOpen(false)} user={viewUser} />
      )}

      <style>{`
        .mfa-toast-dim{
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,.30);
          z-index: 99998;
        }
        /* Default: no dim on desktop */
        @media (min-width: 701px){
          .mfa-toast-dim{ display: none; }
        }
        .mfa-toast{
          position: fixed;
          right: 16px;
          bottom: 16px;
          z-index: 99999;
          width: min(380px, calc(100vw - 24px));
          pointer-events: none;
        }
        .mfa-toast__inner{
          pointer-events: auto;
          border-radius: 16px;
          border: 1px solid rgba(42,5,22,.14);
          background: rgba(255,255,255,.96);
          backdrop-filter: blur(8px);
          box-shadow: 0 18px 48px rgba(0,0,0,.14);
          padding: 12px 12px 12px 14px;
          display:flex;
          flex-direction:column;
          gap:6px;
        }
        .mfa-toast__title{
          font-weight: 1000;
          color:#2a0516;
          letter-spacing: .2px;
        }
        .mfa-toast__text{
          color:#657899;
          font-weight:700;
          line-height:1.25;
        }
        .mfa-toast__actions{
          display:flex;
          gap:10px;
          align-items:center;
          justify-content:space-between;
          margin-top:6px;
        }
        .mfa-toast__btn{
          height: 42px;
          display:inline-flex;
          align-items:center;
          justify-content:center;
          padding: 0 14px;
          border-radius: 12px;
          font-weight: 950;
          text-decoration:none;
          background:#2a0516;
          color:#fff;
          border:1px solid #2a0516;
          box-shadow:0 10px 24px rgba(0,0,0,.10);
          transition:transform .12s ease, box-shadow .12s ease, filter .12s ease;
          white-space:nowrap;
          flex: 1 1 auto;
        }
        .mfa-toast__btn:hover{
          transform: translateY(-1px);
          box-shadow:0 14px 28px rgba(0,0,0,.14);
          filter: brightness(1.02);
        }
        .mfa-toast__btn:active{
          transform: translateY(0px);
          box-shadow:0 10px 24px rgba(0,0,0,.10);
          filter: brightness(.99);
        }
        .mfa-toast__close{
          width: 42px;
          height: 42px;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
          background:#fff;
          color:#111827;
          font-size: 22px;
          line-height: 1;
          cursor:pointer;
          box-shadow:0 10px 24px rgba(0,0,0,.06);
          transition:transform .12s ease, box-shadow .12s ease;
          flex: 0 0 auto;
        }
        .mfa-toast__close:hover{
          transform: translateY(-1px);
          box-shadow:0 14px 28px rgba(0,0,0,.08);
        }
        .mfa-toast__close:active{transform: translateY(0px);}

        /* Mobile: avoid footer overlap by pinning near top (below header) */
        @media (max-width: 700px){
          .mfa-toast{
            left: 12px;
            right: 12px;
            bottom: auto;
            top: 74px;
            width: auto;
          }
          .mfa-toast__inner{padding: 12px;}
          .mfa-toast__actions{gap:8px}
          .mfa-toast__btn{width: 100%;}
        }

        .sh-wrap{min-height:100vh;display:flex;flex-direction:column}
        .sh-body{
          width:100%;
          max-width:1200px;
          margin:0 auto;
          padding:0 0 16px;
          display:grid;
          grid-template-columns:${isDesktop && !isNursingHomeAccount ? "280px 1fr" : "1fr"};
          gap:0;
          align-items:start;
        }
        .sh-main{ min-width:0; padding:0px 0px; }
        @media (max-width: 1023px){
          .sh-body{display:block}
          .sh-main{padding:0px 0px}
        }
      `}</style>

      <MobileFooter />
      <MfaGate user={viewUser} hardGateForAdmins rememberDays={30} />
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);

  // Auth bootstrap
  useEffect(() => {
    let unsubUserDoc = null;

    const unsubAuth = onAuthStateChanged(auth, async (authUser) => {
      if (window.__lwb_signup_in_progress) return;
      setProfileLoading(true);
      try {
        if (typeof unsubUserDoc === "function") {
          unsubUserDoc();
          unsubUserDoc = null;
        }

        if (!authUser) {
          clearSessionBypass();
          setUser(null);
          setAuthChecked(true);
          setProfileLoading(false);
          return;
        }

        const userRef = doc(db, "users", authUser.uid);
        let snap = await getDoc(userRef);

        if (!snap.exists()) {
          const nowMs = Date.now();
          await setDoc(userRef, {
            username: authUser.email,
            email: authUser.email,
            name: "",
            phone: "",
            address: "",
            subscriptionStatus: "pending",
            createdAtMs: nowMs,
            mfaPolicyGraceDays: MFA_GRACE_DAYS,
            mfaRequiredAtMs: nowMs + MFA_GRACE_MS,
          });
          snap = await getDoc(userRef);
        }

        const ensureBaseFields = async (raw) => {
          const nowMs = Date.now();
          const createdAtMs =
            raw.createdAtMs ||
            (raw.createdAt && typeof raw.createdAt.toMillis === "function"
              ? raw.createdAt.toMillis()
              : null) ||
            nowMs;

          const mfaRequiredAtMs = raw.mfaRequiredAtMs || createdAtMs + MFA_GRACE_MS;

          const patch = {};
          if (!raw.createdAtMs) patch.createdAtMs = createdAtMs;
          if (!raw.mfaPolicyGraceDays) patch.mfaPolicyGraceDays = MFA_GRACE_DAYS;
          if (!raw.mfaRequiredAtMs) patch.mfaRequiredAtMs = mfaRequiredAtMs;

          if (Object.keys(patch).length) {
            try {
              await updateDoc(userRef, patch);
            } catch {}
          }

          return { ...raw, ...patch };
        };

        const buildAndSetUser = async (rawProfile) => {
          const userProfile = await ensureBaseFields(rawProfile || {});

          await authUser.getIdToken(true);
          const { claims } = await getIdTokenResult(authUser);

          const resolvedTenantId =
            userProfile.primaryTenantId ||
            userProfile.tenantId ||
            "";

          let tenantBillingStatus = String(userProfile.tenantBillingStatus || "").toLowerCase();
          try {
            if (resolvedTenantId) {
              const tenantSnap = await getDoc(doc(db, "tenants", resolvedTenantId));
              const tenantData = tenantSnap.exists() ? (tenantSnap.data() || {}) : {};
              const liveTenantBillingStatus = String(tenantData.billingStatus || "").toLowerCase();
              if (liveTenantBillingStatus) {
                tenantBillingStatus = liveTenantBillingStatus;
              }
            }
          } catch {}

          const hydratedBusinessAccount = {
            ...(userProfile.businessAccount || {}),
            tenantId:
              (userProfile.businessAccount && userProfile.businessAccount.tenantId) ||
              userProfile.primaryTenantId ||
              userProfile.tenantId ||
              "",
            role:
              (userProfile.businessAccount && userProfile.businessAccount.role) ||
              userProfile.tenantRole ||
              (
                ["owner", "hr_admin", "employee"].includes(String(userProfile.role || "").toLowerCase())
                  ? String(userProfile.role || "").toLowerCase()
                  : ""
              ),
            billingStatus:
              (userProfile.businessAccount && userProfile.businessAccount.billingStatus) ||
              tenantBillingStatus ||
              "",
          };

          const next = {
            ...userProfile,
            uid: authUser.uid,
            email: authUser.email,
            username: authUser.email,
            isAdmin:
              !!claims.admin ||
              !!userProfile.isAdmin ||
              userProfile.role === "platform_admin",
            emailVerified: !!authUser.emailVerified,
            subscriptionStatus: userProfile.subscriptionStatus || "pending",
            managerOnly: !!userProfile.managerOnly,
            role: userProfile.role,

            // legacy-compatible flattened fields
            tenantRole:
              userProfile.tenantRole ||
              hydratedBusinessAccount.role ||
              "",
            tenantId:
              userProfile.tenantId ||
              hydratedBusinessAccount.tenantId ||
              "",
            primaryTenantId:
              userProfile.primaryTenantId ||
              hydratedBusinessAccount.tenantId ||
              "",
            tenantBillingStatus,

            // unified account model
            accountTypes: userProfile.accountTypes || {},
            personalAccount: userProfile.personalAccount || {},
            businessAccount: hydratedBusinessAccount,
            managerAccess: userProfile.managerAccess || {},

            accountType: userProfile.accountType || "",
            suspended: !!userProfile.suspended,
          };

          try {
            if (next.isAdmin || next.subscriptionStatus === "active") {
              sessionStorage.removeItem("amOnboarding");
            }
          } catch {}

          setUser(next);
          setAuthChecked(true);
          setProfileLoading(false);
        };

        await buildAndSetUser(snap.data() || {});

        unsubUserDoc = onSnapshot(
          userRef,
          async (liveSnap) => {
            await buildAndSetUser(liveSnap.exists() ? (liveSnap.data() || {}) : {});
          },
          async () => {
            const fallbackSnap = await getDoc(userRef).catch(() => null);
            await buildAndSetUser(fallbackSnap?.exists() ? (fallbackSnap.data() || {}) : {});
          }
        );
      } catch (e) {
        console.error("Auth bootstrap failed:", e);
        setUser(null);
        setAuthChecked(true);
        setProfileLoading(false);
      }
    });

    return () => {
      if (typeof unsubUserDoc === "function") unsubUserDoc();
      if (typeof unsubAuth === "function") unsubAuth();
    };
  }, []);

  async function handleProfileUpdate(newProfile) {
    if (!user) return;
    try {
      const userRef = doc(db, "users", user.uid);
      const docSnap = await getDoc(userRef);
      const username = user.username || user.email || "";
      if (!docSnap.exists()) {
        await setDoc(userRef, {
          username,
          name: newProfile.name,
          phone: newProfile.phone,
          address: newProfile.address,
          email: newProfile.email,
        });
      } else {
        await updateDoc(userRef, {
          name: newProfile.name,
          phone: newProfile.phone,
          address: newProfile.address,
          email: newProfile.email,
        });
      }
      const fresh = await getDoc(userRef);
      if (fresh.exists())
        setUser((u) => ({ ...u, ...fresh.data(), username, email: fresh.data().email || u.email }));
    } catch (e) {
      alert("Failed to update profile: " + e.message);
      throw e;
    }
  }

  function handleLogout() {
    try {
      sessionStorage.removeItem("amOnboarding");
    } catch {}
    clearSessionBypass(user?.uid);
    signOut(auth);
    setUser(null);
  }

  if (user && user.suspended) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div
          style={{
            background: "#fff",
            borderRadius: 8,
            boxShadow: "0 2px 14px #22091a12",
            padding: 40,
            color: "#a00",
            fontWeight: 500,
          }}
        >
          Your account is suspended.
          <br />
          Please contact support to reactivate.
        </div>
      </div>
    );
  }

  if (!authChecked || profileLoading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f6f7fb" }}>
        <div style={{ textAlign: "center" }}>
          <img src="/Lastwishbox-logo.png" alt="LastWish Box" style={{ height: 48, marginBottom: 24 }} />
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div style={{
              width: 36, height: 36, borderRadius: "50%",
              border: "3px solid #f0e8ec", borderTopColor: "#2a0516",
              animation: "lwb-spin 0.8s linear infinite"
            }} />
          </div>
          <style>{"@keyframes lwb-spin { to { transform: rotate(360deg); } }"}</style>
        </div>
      </div>
    );
  }

  const isManagerOnly = !!user && (user.managerOnly === true || user.role === "account_manager");

  const businessTenantId =
    user?.businessAccount?.tenantId ||
    user?.primaryTenantId ||
    user?.tenantId ||
    "";

  const businessRole = String(
    user?.businessAccount?.role ||
    user?.tenantRole ||
    user?.role ||
    ""
  ).toLowerCase();

  const businessBillingStatus = String(
    user?.businessAccount?.billingStatus ||
    user?.tenantBillingStatus ||
    ""
  ).toLowerCase();

  const isTenantOwnerOrAdmin =
    !!user &&
    !!businessTenantId &&
    ["owner", "hr_admin"].includes(businessRole);

  const isBusinessUser =
    !!user &&
    !!businessTenantId;

  const hasPaidBusinessAccess =
    !!user &&
    (
      user?.businessAccount?.active === true ||
      (
        isBusinessUser &&
        ["active", "trial"].includes(businessBillingStatus)
      )
    );

  const hasLegacyAccess =
    !!user &&
    (
      user.isAdmin ||
      user.subscriptionStatus === "active" ||
      user.subscriptionStatus === "free" ||
      user?.personalAccount?.active === true
    );

  const canAccessApp =
    !!user &&
    (
      hasLegacyAccess ||
      hasPaidBusinessAccess ||
      (isTenantOwnerOrAdmin && ["active", "trial"].includes(businessBillingStatus))
    );

  // ✅ MFA policy: recommend MFA (no forced enrollment / redirects)
  const mfaEnabled = !!user?.mfa?.enabled;

  return (
    <AccessProvider>
      <Router>
        <AnalyticsRouteTracker />

        <Routes>
          {/* ADMIN */}
          <Route
            path="/admin"
            element={
              <RequireAdmin user={user} authReady={authChecked}>
                <AdminDashboard />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/metrics"
            element={<AdminMetrics />}
          />
          <Route
            path="/admin/analytics"
            element={
              <RequireAdmin user={user} authReady={authChecked}>
                <AdminAnalytics />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/leads"
            element={
              <RequireAdmin user={user} authReady={authChecked}>
                <AdminLeads />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/referrals"
            element={
              <RequireAdmin user={user} authReady={authChecked}>
                <AdminReferrals />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/nursing-homes"
            element={
              <RequireAdmin user={user} authReady={authChecked}>
                <AdminNursingHomes />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/support"
            element={
              <RequireAdmin user={user} authReady={authChecked}>
                <AdminTickets />
              </RequireAdmin>
            }
          />

          <Route
            path="/admin/tenant/:tenantId"
            element={
              <RequireAdmin user={user} authReady={authChecked}>
                <TenantDetail user={user} />
              </RequireAdmin>
            }
          />
          <Route path="/accept-invite" element={<AcceptTenantInvite />} />

          {/* Always reachable */}
          <Route path="/accept-invitation" element={<AcceptInvitation />} />
          <Route path="/am/accept" element={<AcceptInvitation />} />
          <Route path="/auth/action" element={<EmailActionHandler />} />
          <Route path="/manager-select" element={<ManagerSelect user={user} />} />
          <Route path="/manager/login/:ownerUid" element={<ManagerLogin />} />
          <Route path="/plans" element={<Pricing />} />
          <Route path="/personal/plans" element={<PersonalPlans />} />
        <Route path="/signup" element={<SignupPage />} />
              <Route path="/family/signup" element={<FamilySignupPage />} />
          <Route path="/post-payment" element={<PostPayment />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Paywall route for non-managers */}
          <Route
            path="/payment-required"
            element={
              !user ? (
                <Navigate to="/login" replace />
              ) : profileLoading ? (
                null
              ) : canAccessApp ? (
                isTenantOwnerOrAdmin && businessTenantId ? (
                  <Navigate to={`/tenant/${businessTenantId}`} replace />
                ) : (
                  <Navigate to="/" replace />
                )
              ) : (
                <PaymentRequired user={user} />
              )
            }
          />

          {/* Public vs Private switch */}
          <Route
            path="/*"
            element={
              !user ? (
                <PublicRoutes onLogin={setUser} />
              ) : canAccessApp ? (
                <PrivateShell
                  user={user}
                  handleProfileUpdate={handleProfileUpdate}
                  handleLogout={handleLogout}
                  handlePasswordReset={() => alert("Password reset flow coming soon!")}
                  sidebarOpen={sidebarOpen}
                  setSidebarOpen={setSidebarOpen}
                  mfaEnabled={mfaEnabled}
                />
              ) : isManagerOnly ? (
                <PrivateShell
                  user={user}
                  handleProfileUpdate={handleProfileUpdate}
                  handleLogout={handleLogout}
                  handlePasswordReset={() => alert("Password reset flow coming soon!")}
                  sidebarOpen={sidebarOpen}
                  setSidebarOpen={setSidebarOpen}
                  mfaEnabled={mfaEnabled}
                />
              ) : profileLoading ? (
                null
              ) : (
                <Navigate to="/payment-required" replace />
              )
            }
          />

          <Route path="/manager/register" element={<ManagerRegister />} />
        </Routes>
      </Router>
    </AccessProvider>
  );
}

export default App;
